import { FC, useCallback, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import P1 from '@base/P1/P1';
import H6 from '@base/H6/H6';
import Image from '@base/Image';
import Close from '@icon/Close';
import { currencySymbolMap, commas } from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import { segment } from '@actions/analytics';
import useCheckoutEventProps from '@hooks/useCheckoutEventProps';
import { IReferralSectionProps } from 'types/checkout';
import Typography from 'aquilla/core/atoms/Typography';
import Button from '@base/Button/Button';
import Drawer from '@base/Drawer/Drawer';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn, getMeCredits } from '@api/selectors/auth';
import useCreditsControl from '@api/hooks/credits/useCreditsControl';
import useWindowSize from '@hooks/useWindowSize';
import ChevronRight from '@icon/ChevronRight';
import ReferralSuggestions from './ReferralSuggestions';
import { getTotalAmtBySubscription } from './usePurchaseDetails';
import ReferralCodeInput from './ReferralCodeInput';
import { CHECKOUT_CREDITS_REDEEMED } from './constants/segment';
import useReferralCodeSuggestions from './useReferralCodeSuggestions';

const getTotalDiscountText = (referral, currency) => {
  if (referral?.message && referral.message.length > 0) {
    return referral.message;
  }
  if (referral?.discountType === 1 && referral?.discountValue > 0) {
    return `Awesome! You got ${currency}${commas(referral.discountValue)} off`;
  }
  if (referral?.discountType === 2 && referral?.discountPercent > 0) {
    return `Awesome! You got ${referral.discountPercent}% off`;
  }
  return null;
};

const InfoContainer = styled.div`
  background-color: var(--color-base-1);
  border-radius: ${Spacings.SPACING_2B};
  padding: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_3B};
  box-shadow: ${boxShadowGenerator()};
  ${mediaQuery.uptoMobile} {
    margin-bottom: 0px;
    padding: ${Spacings.SPACING_4B};
  }
`;

const ReferralInfoContainer = styled(InfoContainer)`
  padding: ${Spacings.SPACING_4B} ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_3B};
  ${mediaQuery.uptoMobile} {
    padding: ${Spacings.SPACING_4B};
    margin-bottom: ${Spacings.SPACING_2B};
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const ReferralInput = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const Divider = styled.div`
  padding-bottom: ${Spacings.SPACING_4B};
  border-bottom: 1px solid var(--color-divider);
`;

const Credits = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: ${Spacings.SPACING_4B};
`;

const StyledP2 = styled.p`
  font-weight: ${Fonts.SEMIBOLD};
  font-size: ${Fonts.P2};
  line-height: ${Fonts.HEADER_LINE_HEIGHT};
  color: ${({ color }) => color};
  margin-top: ${Spacings.SPACING_2B};
  margin-left: ${Spacings.SPACING_10B};
  ${mediaQuery.uptoTablet} {
    margin-left: 30px;
  }
  ${mediaQuery.uptoMobile} {
    font-size: 10px;
  }
`;

const CreditsText = styled(H6)`
  flex: 1;
  margin-left: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_4B};
`;

const CreditsApplied = styled(P1)`
  display: inline;
`;

const Redeem = styled(H6)`
  cursor: pointer;
`;

const CloseIcon = styled(Close)`
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const SeeAllBtn = styled(Button)`
  width: 74px;
  height: 32px;
  min-height: 32px;
  font-size: ${Fonts.H7};
  line-height: 14px;
`;

const StyledDrawer = styled(Drawer)`
  &.MuiDrawer-root > .MuiPaper-root {
    padding: 0;
  }

  ${mediaQuery.aboveTablet} {
    &.MuiDrawer-root > .MuiPaper-root {
      padding: 0;
      width: 620px;
    }
  }
`;

const ReferralSuccessText = styled(H6)<{ $mwebCheckoutV2: boolean }>`
  flex: 1;
  margin-left: ${Spacings.SPACING_4B};
  margin-right: ${Spacings.SPACING_4B};
  font-weight: 400;
  ${mediaQuery.uptoMobile} {
    ${({ $mwebCheckoutV2 }) => {
      if ($mwebCheckoutV2)
        return `
      display: flex;
      flex-direction: column;
        `;
    }}
  }
`;

const Highlight = styled.span`
  font-weight: 600;
  font-size: ${Fonts.SMALL};
`;

const HighlightSubheading = styled.span`
  color: var(--color-text-secondary);
  font-size: 12px;
  font-weight: 400;
`;

const ClearButton = styled(Close)`
  cursor: pointer;
  width: ${Spacings.SPACING_6B};
  height: ${Spacings.SPACING_6B};
`;

const WrapperContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: ${Spacings.SPACING_2B};
`;

const LeftWrapper = styled.div`
  display: flex;
  gap: ${Spacings.SPACING_2B};
  align-items: center;
`;

const RightWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CodeLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
`;

const DiscountLabel = styled.div`
  background: #e6f8f2;
  padding: 2px 6px;
  color: var(--color-i-green);
  font-weight: 700;
  font-size: 10px;
  border-radius: ${Spacings.SPACING_1B};
  text-transform: uppercase;
`;

const LabelArrow = styled.div``;

const ReferralSection: FC<IReferralSectionProps> = ({
  isReferralPreapplied = false,
  onApply,
  resetReferral,
  hideCredits = false,
  referralInputRef = null,
  applyCredits = () => {},
  removeCredits = () => {},
  isCombo = false,
  subscription,
  discountedSubscription,
  referral,
  credits,
  creditsApplied = false,
  goal,
  referralError,
  referralSubmitted,
  isReferralValid,
  isSuggestionFlow = false,
  validateReferralInProgress = false,
  mwebCheckoutV2 = false
}) => {
  const { referralCodes } = useReferralCodeSuggestions(subscription?.uid);
  const successMessage =
    !!discountedSubscription &&
    getTotalDiscountText(
      discountedSubscription?.referral,
      currencySymbolMap(subscription?.currency || 'INR')
    );
  const { isMobile } = useWindowSize();
  const userCredits = useMyInfo(getMeCredits);
  const { isCreditsEnabledOnPlatform } = useCreditsControl();

  let referralErrorMessage = referralError?.message;
  const UppercaseReferral = referral?.toUpperCase();
  if (
    discountedSubscription &&
    !discountedSubscription?.referral?.referralType
  ) {
    referralErrorMessage = 'Code not applicable on the selected plan';
  }

  const maxDiscount = useMemo(() => {
    if (!referralCodes?.length) return 0;
    const unExpiredCodes = referralCodes.filter(
      ({ validTill }) => !validTill || new Date(validTill) > new Date()
    );
    if (unExpiredCodes.length) return unExpiredCodes[0].discountPercent;
    return 0;
  }, [referralCodes]);

  const referralMessage = !isReferralValid
    ? referralErrorMessage
    : successMessage;

  const appliedSubscription =
    referral && !referralError
      ? discountedSubscription?.subscription
      : subscription;

  const eventProps = useCheckoutEventProps(
    goal?.uid,
    appliedSubscription,
    isCombo
  );

  const apply = () => {
    applyCredits();
    segment.track(CHECKOUT_CREDITS_REDEEMED, {
      ...eventProps,
      referral_code: referral || 'NA',
      is_referral_successful: Boolean(referral) && !referralError,
      has_applied_credits: true,
      credits_applied: credits,
      current_credits: userCredits,
      subscription_price: getTotalAmtBySubscription(subscription, true)
    });
  };

  const [showCouponsModal, setShowCouponsModal] = useState(false);

  const closeCouponsModal = useCallback(() => {
    setShowCouponsModal(false);
  }, [setShowCouponsModal]);

  const handleMobileInputClick = () => {
    setShowCouponsModal(true);
  };

  const isLoggedIn = useMyInfo(getIsLoggedIn);

  return (
    <ReferralInfoContainer>
      {(!mwebCheckoutV2 || !isMobile) && isSuggestionFlow && isLoggedIn && (
        <Header>
          <Typography variant="h5">Coupons and offers</Typography>
          {(!referralSubmitted ||
            validateReferralInProgress ||
            !isReferralValid) && (
            <SeeAllBtn
              // @ts-ignore
              label="SEE ALL"
              variant="hollow"
              color="black"
              onClick={() => setShowCouponsModal(true)}
            />
          )}
        </Header>
      )}
      <ReferralInput>
        {!(
          referralSubmitted &&
          !validateReferralInProgress &&
          isReferralValid
        ) &&
          (mwebCheckoutV2 && isMobile ? (
            <WrapperContainer onClick={handleMobileInputClick}>
              <LeftWrapper>
                <Image
                  src="checkout/discount.svg"
                  alt="discount"
                  height={24}
                  width={24}
                />
                <CodeLabel>Coupons and offers</CodeLabel>
              </LeftWrapper>
              <RightWrapper>
                {maxDiscount > 0 && (
                  <DiscountLabel>{`up to ${maxDiscount}% off`}</DiscountLabel>
                )}

                <LabelArrow>
                  <ChevronRight size="24px" color="var(--color-i-black)" />
                </LabelArrow>
              </RightWrapper>
            </WrapperContainer>
          ) : (
            <ReferralCodeInput
              error={
                referralSubmitted && !isReferralValid && !!referralErrorMessage
              }
              color={
                referralSubmitted && isReferralValid ? 'secondary' : 'primary'
              }
              onApply={onApply}
              resetReferral={resetReferral}
              referralSubmitted={referralSubmitted}
              defaultValue={isReferralPreapplied ? referral : null}
              // @ts-ignore
              ref={referralInputRef}
            />
          ))}
        {referralSubmitted &&
          !validateReferralInProgress &&
          isReferralValid && (
            <>
              <Image
                src="checkout/discount.svg"
                alt=""
                height={24}
                width={24}
              />
              <ReferralSuccessText $mwebCheckoutV2={mwebCheckoutV2}>
                {mwebCheckoutV2 && isMobile ? (
                  <Highlight>&#39;{UppercaseReferral}&#39; applied</Highlight>
                ) : (
                  <>
                    <Highlight>&#39;{UppercaseReferral}&#39;</Highlight> applied
                  </>
                )}

                {mwebCheckoutV2 && isMobile && (
                  <HighlightSubheading>
                    Offer applied on the final bill{' '}
                  </HighlightSubheading>
                )}
              </ReferralSuccessText>
              <ClearButton
                data-testid="clearReferral"
                size="24px"
                onClick={resetReferral}
              />
            </>
          )}
      </ReferralInput>
      <>
        {(!isMobile || !mwebCheckoutV2) &&
        referral &&
        !isReferralValid &&
        referralMessage !== '' ? (
          <StyledP2 color="var(--color-i-red)" role="referral-message-invalid">
            {referralMessage}
          </StyledP2>
        ) : null}
      </>
      {!isReferralValid &&
        !hideCredits &&
        credits > 0 &&
        isCreditsEnabledOnPlatform && <Divider />}
      {!hideCredits && credits > 0 && isCreditsEnabledOnPlatform && (
        <Credits>
          <Image src="credit.png" alt="credits" width={24} height={24} />
          <CreditsText
            role={
              creditsApplied
                ? 'credits-text-applied'
                : 'credits-text-notapplied'
            }
          >
            {`${commas(credits)} ${!creditsApplied ? 'credits' : ''}`}
            {creditsApplied && <CreditsApplied>credits applied</CreditsApplied>}
          </CreditsText>
          {creditsApplied ? (
            <CloseIcon size="24px" onClick={removeCredits} />
          ) : (
            <Redeem color="var(--color-i-green)" onClick={apply}>
              Redeem
            </Redeem>
          )}
        </Credits>
      )}
      <StyledDrawer
        drawerState={showCouponsModal}
        hideCloseButton
        onClose={closeCouponsModal}
      >
        <ReferralSuggestions
          onClose={closeCouponsModal}
          onApply={onApply}
          referralCode={referral}
          validateReferralInProgress={validateReferralInProgress}
          isReferralValid={isReferralValid}
          referralMessage={referralMessage}
          isSuggestionFlow={isSuggestionFlow}
          isPrimeSubscription={subscription?.contentTypeId === 507}
        />
      </StyledDrawer>
    </ReferralInfoContainer>
  );
};

export default ReferralSection;
